import {
  Badge,
  BadgeColor,
  BadgeContainer,
} from '@delmonicos/shared-design-system-react/src/components/badge/badge';
import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import {
  Dialog,
  DialogPosition,
} from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import {
  ResourceStatus,
  withProtectedResource,
} from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { GetRfidBadgeOrdersPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/emsp-bo/models/inputs/rfid-badge-orders/get-rfid-badge-orders-paginated.model';
import { safeHasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { transformPaginatedResponse } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/transform-api-paginated-response/transform-api-response';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import {
  OnChangeFn,
  SortingState,
  functionalUpdate,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { permissions } from '@app/core/authentication/authentication.slice';
import { getState } from '@app/core/helper/get-state';
import { useAppSelector } from '@app/core/hooks';
import { useEmspBoGetRfidBadgeOrdersPaginatedQuery } from '@app/core/service/emsp-bo-rfid-badge-order.service';
import {
  createColumnOrderChangeHandler,
  createHideColumnHandler,
} from '@shared/list-options/column-options.helper';

import { EmspBoRfidBadgeOrderListFilterSearchController } from '../emsp-bo-rfid-badge-order-list-filter-search/emsp-bo-rfid-badge-order-list-filter-search.controller';
import { EmspBoRfidBadgeOrderListFilterStateController } from '../emsp-bo-rfid-badge-order-list-filter-state/emsp-bo-rfid-badge-order-list-filter-state.controller';
import { EmspBoRfidBadgeOrderListFilterController } from '../emsp-bo-rfid-badge-order-list-filter/emsp-bo-rfid-badge-order-list-filter.controller';
import { EmspBoRfidBadgeOrderListOptionsController } from '../emsp-bo-rfid-badge-order-list-options/emsp-bo-rfid-badge-order-list-options.controller';
import {
  EmspBoRfidBadgeOrderListContextProvider,
  useEmspBoRfidBadgeOrderListContext,
} from '../emsp-bo-rfid-badge-order.context';
import {
  EMSP_BO_RFID_BADGE_ORDER_LIST_FILTER_NAME_LIST,
  EMSP_BO_RFID_BADGE_ORDER_LIST_SORT_FIELD,
} from './emsp-bo-rfid-badge-order-list.config';
import { EmspBoRfidBadgeOrderListView } from './emsp-bo-rfid-badge-order-list.view';

export const EmspBoRfidBadgeOrderListController = (): React.JSX.Element => {
  // CONTEXT /////////////////////////////////////////////////////////////////

  const {
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    getPaginatedLimitList,
    columnOptions,
    setColumnOptions,
  } = useEmspBoRfidBadgeOrderListContext();

  // USER PERMISSIONS ////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  // ACTIONS /////////////////////////////////////////////////////////////////

  const [actionFilterIsDialogOpen, setActionFilterIsDialogOpen] =
    useState(false);

  // DATA FETCHING ///////////////////////////////////////////////////////////

  const queryState = useEmspBoGetRfidBadgeOrdersPaginatedQuery(
    getPaginatedQueryParameters,
  );

  const paginationState = useMemo(
    () =>
      getState({
        isLoading: queryState.isLoading,
        isError: queryState.isError,
        isFetching: queryState.isFetching,
      }),
    [queryState.isLoading, queryState.isError, queryState.isFetching],
  );

  const filterNameActiveList =
    EMSP_BO_RFID_BADGE_ORDER_LIST_FILTER_NAME_LIST.filter((filterName) => {
      const value = getPaginatedQueryParameters[filterName];
      return Array.isArray(value) ? value.length > 0 : value !== undefined;
    });

  const filterActiveHiddenCount = Math.max(
    0,
    filterNameActiveList.reduce((count, filterName) => {
      const filterValue = getPaginatedQueryParameters[filterName] as any;

      if (Array.isArray(filterValue)) {
        return count + filterValue.length;
      }

      return count + 1;
    }, 0) - 3,
  );

  // PAGE TITLE //////////////////////////////////////////////////////////////

  // Nothing to do

  // PAGE ACTIONS ////////////////////////////////////////////////////////////

  // Nothing to do

  // PAGE BREADCRUMB /////////////////////////////////////////////////////////

  // Nothing to do

  // HANDLERS ////////////////////////////////////////////////////////////////

  const handlePaginationPageChange = (paginationPageNext: number): void => {
    setGetPaginatedQueryParameters((current) => ({
      ...current,
      offset: (paginationPageNext - 1) * current.limit,
    }));
  };

  const handlePaginationLimitChange = (paginationLimitNext: number): void => {
    setGetPaginatedQueryParameters((current) => ({
      ...current,
      limit: paginationLimitNext,
      offset: 0,
    }));
  };

  const handlePaginationSortingChange: OnChangeFn<SortingState> = (
    sortingState,
  ): void => {
    setGetPaginatedQueryParameters((current) => {
      const paginationSorting0 = functionalUpdate(sortingState, [
        {
          desc: current.sortOrder === -1,
          id: current.sortField ?? EMSP_BO_RFID_BADGE_ORDER_LIST_SORT_FIELD,
        },
      ]).at(0);

      return {
        ...current,
        sortField:
          paginationSorting0?.id as GetRfidBadgeOrdersPaginatedModel['sortField'],
        sortOrder: paginationSorting0?.desc ? -1 : 1,
      };
    });
  };

  const handleHideColumn = createHideColumnHandler(setColumnOptions);
  const handleColumnOrderChange =
    createColumnOrderChangeHandler(setColumnOptions);

  // RENDER //////////////////////////////////////////////////////////////////

  const {
    Component: EmspBoRfidBadgeOrderListViewProtected,
    status: resourceStatus,
  } = withProtectedResource(EmspBoRfidBadgeOrderListView, queryState, {
    isAuthorized: () =>
      safeHasAccessTo(userPermissions, {
        resourceName: Resource.EMSP_BO,
        action: ActionsByResource.EMSP_BO.READ_RFID_BADGE_ORDER,
      }),
    transformDataToResource: transformPaginatedResponse,
  })({
    paginationLimit: getPaginatedQueryParameters.limit,
    paginationLimitList: getPaginatedLimitList,
    paginationState,
    paginationSorting: [
      {
        desc: getPaginatedQueryParameters.sortOrder === -1,
        id:
          getPaginatedQueryParameters.sortField ??
          EMSP_BO_RFID_BADGE_ORDER_LIST_SORT_FIELD,
      },
    ],
    columnVisibility: columnOptions.columnVisibility,
    columnOrder: columnOptions.columnOrder,
    renderColumnSettings: () => <EmspBoRfidBadgeOrderListOptionsController />,
    onPaginationLimitChange: handlePaginationLimitChange,
    onPaginationPageChange: handlePaginationPageChange,
    onPaginationSortingChange: handlePaginationSortingChange,
    onHideColumn: handleHideColumn,
    onColumnOrderChange: handleColumnOrderChange,
  });

  const { t } = useTranslation();

  return (
    <Stack flex={1} direction="column">
      {resourceStatus === ResourceStatus.SUCCESS && (
        <Stack direction="row" justifyContent="space-between" gap={GapEnum.XL}>
          <Stack flex={1}>
            <EmspBoRfidBadgeOrderListFilterSearchController
              onCancel={() => {}}
              onSuccess={() => {}}
            />
          </Stack>
          <EmspBoRfidBadgeOrderListFilterStateController />
          <Stack justifyContent="end" gap={GapEnum.M}>
            <BadgeContainer
              badge={
                filterActiveHiddenCount > 0 && (
                  <Badge color={BadgeColor.Secondary}>
                    {`+${filterActiveHiddenCount}`}
                  </Badge>
                )
              }>
              <ButtonIcon
                icon="filter-funnel-01"
                onClick={() => setActionFilterIsDialogOpen(true)}
              />
            </BadgeContainer>
          </Stack>
        </Stack>
      )}
      <Stack>{EmspBoRfidBadgeOrderListViewProtected}</Stack>
      {resourceStatus === ResourceStatus.SUCCESS && (
        <Dialog
          open={actionFilterIsDialogOpen}
          onClose={() => setActionFilterIsDialogOpen(false)}
          position={DialogPosition.RIGHT}>
          <Dialog.Header
            icon="filter-funnel-01"
            title={t('$.emsp-bo-rfid-badge-order-list-filter.title')}
            onClose={() => setActionFilterIsDialogOpen(false)}
          />
          <Dialog.Content>
            <EmspBoRfidBadgeOrderListFilterController
              onCancel={() => setActionFilterIsDialogOpen(false)}
              onSuccess={() => setActionFilterIsDialogOpen(false)}
            />
          </Dialog.Content>
        </Dialog>
      )}
    </Stack>
  );
};

export const EmspBoRfidBadgeOrderListControllerWithContext =
  (): React.JSX.Element => {
    return (
      <EmspBoRfidBadgeOrderListContextProvider>
        <EmspBoRfidBadgeOrderListController />
      </EmspBoRfidBadgeOrderListContextProvider>
    );
  };
